import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import PageBanner from '@/components/base/Layout/PageBanner/PageBanner';
import { ExternalLinks } from '@/config/externalLinks';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { usePageViewTracker } from '@/hooks/usePageViewTracker';
import Link from '@/storybook/components/Link/Link';
import Text from '@/storybook/components/Text/Text';
import { AppRoutes } from '@/utils/constants';

import styles from './SalesAccessRestricted.module.css';
import { SALES_ACCESS_RESTRICTED_TEST_IDS } from './testIds';

type AccessStepKey = `main.sales.accessRestricted.step${1 | 2 | 3 | 4 | 5 | 6 | 7}`;

interface AccessStep {
  translationKey: AccessStepKey;
  renderSuffix?: () => ReactNode;
}

const STEPS: AccessStep[] = [
  { translationKey: 'main.sales.accessRestricted.step1' },
  {
    translationKey: 'main.sales.accessRestricted.step2',
    renderSuffix: () => (
      <>
        {' '}
        <Link
          to={ExternalLinks.CIDM}
          isExternal
          appearance="ghost"
          size="S"
          className={styles.inlineLink}
          data-testid={SALES_ACCESS_RESTRICTED_TEST_IDS.cidmLink}
        >
          {ExternalLinks.CIDM}
        </Link>
      </>
    ),
  },
  { translationKey: 'main.sales.accessRestricted.step3' },
  { translationKey: 'main.sales.accessRestricted.step4' },
  { translationKey: 'main.sales.accessRestricted.step5' },
  { translationKey: 'main.sales.accessRestricted.step6' },
  { translationKey: 'main.sales.accessRestricted.step7' },
];

const SalesAccessRestricted: FC = () => {
  const { t } = useTranslation();

  usePageViewTracker({ pageIdentifier: AdobeAnalyticsConst.PAGE_IDENTIFIER.SALES });

  return (
    <>
      <PageBanner.Navigation />
      <PageBanner.Title>{t('main.sales.title')}</PageBanner.Title>
      <div className={styles.container} data-testid={SALES_ACCESS_RESTRICTED_TEST_IDS.container}>
        <Text as="h2" className={styles.stepsTitle} data-testid={SALES_ACCESS_RESTRICTED_TEST_IDS.stepsTitle}>
          {t('main.sales.accessRestricted.stepsTitle')}
        </Text>
        <ol className={styles.steps} data-testid={SALES_ACCESS_RESTRICTED_TEST_IDS.steps}>
          {STEPS.map(({ translationKey, renderSuffix }) => (
            <li key={translationKey}>
              {t(translationKey)}
              {renderSuffix?.()}
            </li>
          ))}
        </ol>
        <Link
          to={AppRoutes.CUSTOMER_FOCUS}
          appearance="secondary"
          size="S"
          data-testid={SALES_ACCESS_RESTRICTED_TEST_IDS.returnLink}
        >
          {t('main.sales.accessRestricted.returnToDashboard')}
        </Link>
      </div>
    </>
  );
};

export default SalesAccessRestricted;
